import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@arcadeai/design-system/components/ui/atoms/collapsible";
import { Skeleton } from "@arcadeai/design-system/components/ui/atoms/skeleton";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tabs";
import { ChevronDown } from "lucide-react";
import type { ReactNode } from "react";
import type { Binding } from "@/app/types";
import { getBindingInfo } from "@/lib/binding";
import { useResourceViewStore } from "./hooks/use-resource-view";

type ResourceSectionConfig = {
  binding: Binding;
  resourceType: string;
};

type ResourceContainerSkeletonProps = {
  tableSkeleton: ReactNode;
  sections?: ResourceSectionConfig[];
  defaultActiveTab?: string;
};

const defaultSections: ResourceSectionConfig[] = [
  { binding: "project", resourceType: "items" },
  { binding: "tenant", resourceType: "items" },
  { binding: "static", resourceType: "items" },
];

/**
 * A generic skeleton component for ResourceContainer that respects the current view type (horizontal/vertical).
 *
 * @example
 * ```tsx
 * // Usage in a list skeleton component
 * const sections = [
 *   { binding: 'project' as const, resourceType: 'servers' },
 *   { binding: 'tenant' as const, resourceType: 'servers' },
 *   { binding: 'static' as const, resourceType: 'servers' },
 * ];
 *
 * <ResourceContainerSkeleton
 *   sections={sections}
 *   tableSkeleton={<YourTableSkeleton />}
 * />
 * ```
 *
 * @param tableSkeleton - The table skeleton component to render inside each section
 * @param sections - Array of section configurations (binding + resourceType). Defaults to project/tenant/static items.
 * @param defaultActiveTab - The default active tab for horizontal view (optional)
 */
export const ResourceContainerSkeleton = ({
  tableSkeleton,
  sections = defaultSections,
  defaultActiveTab,
}: ResourceContainerSkeletonProps) => {
  const { viewType } = useResourceViewStore();

  if (viewType === "horizontal") {
    return (
      <HorizontalResourceSkeleton
        defaultActiveTab={defaultActiveTab}
        sections={sections}
        tableSkeleton={tableSkeleton}
      />
    );
  }

  return (
    <VerticalResourceSkeleton
      sections={sections}
      tableSkeleton={tableSkeleton}
    />
  );
};

type HorizontalResourceSkeletonProps = {
  sections: ResourceSectionConfig[];
  tableSkeleton: ReactNode;
  defaultActiveTab?: string;
};

const HorizontalResourceSkeleton = ({
  sections,
  tableSkeleton,
  defaultActiveTab,
}: HorizontalResourceSkeletonProps) => {
  const firstTabValue =
    defaultActiveTab || `${sections[0]?.binding}-${sections[0]?.resourceType}`;

  return (
    <div className="w-full">
      <Tabs className="w-full" defaultValue={firstTabValue}>
        <TabsList className="inline-flex h-11 items-center justify-start rounded-lg bg-gradient-to-b from-muted/40 to-muted/60 px-2 text-muted-foreground backdrop-blur-sm">
          {sections.map((section) => {
            const bindingInfo = getBindingInfo(section.binding);
            const { icon: Icon, classes } = bindingInfo;
            const tabValue = `${section.binding}-${section.resourceType}`;

            return (
              <TabsTrigger
                className="group relative h-8 cursor-pointer items-center gap-2 rounded-md py-1 dark:border-none"
                disabled
                key={tabValue}
                value={tabValue}
              >
                <div className="relative flex size-5 items-center justify-center rounded-md">
                  <Icon className={`size-3.5 ${classes.sectionIcon}`} />
                </div>
                <span className="font-medium">{bindingInfo.displayName}</span>
                <Skeleton className="ml-1 h-5 w-6 rounded-full" />
              </TabsTrigger>
            );
          })}
        </TabsList>

        <div className="mt-1">
          {sections.map((section) => {
            const tabValue = `${section.binding}-${section.resourceType}`;

            return (
              <TabsContent
                className="mt-0 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                key={tabValue}
                value={tabValue}
              >
                {tableSkeleton}
              </TabsContent>
            );
          })}
        </div>
      </Tabs>
    </div>
  );
};

type VerticalResourceSkeletonProps = {
  sections: ResourceSectionConfig[];
  tableSkeleton: ReactNode;
};

const VerticalResourceSkeleton = ({
  sections,
  tableSkeleton,
}: VerticalResourceSkeletonProps) => (
  <div className="space-y-4">
    {sections.map((section, index) => {
      const bindingInfo = getBindingInfo(section.binding);
      const { icon: Icon, classes, description } = bindingInfo;
      const title = `${bindingInfo.displayName} ${section.resourceType}`;

      return (
        <Collapsible
          className="w-full rounded-lg border"
          defaultOpen={index === 0}
          key={`${section.binding}-${section.resourceType}`}
        >
          <CollapsibleTrigger
            render={
              <button
                className="group flex w-full items-center gap-3 rounded-t-lg px-4 py-3 transition-colors hover:bg-muted/50"
                type="button"
              />
            }
          >
            <div
              className={`flex size-10 items-center justify-center rounded-md ${classes.sectionBackground}`}
            >
              <Icon className={`size-5 ${classes.sectionIcon}`} />
            </div>
            <div className="flex flex-1 flex-col">
              <div className="flex items-center gap-2">
                <h2
                  className={`font-semibold text-base ${classes.sectionTitle}`}
                >
                  {title}
                </h2>
                <span className="flex items-center gap-1 text-muted-foreground text-sm">
                  (<Skeleton className="inline-block h-4 w-4" />)
                </span>
              </div>
              <p className="text-left text-muted-foreground text-xs leading-relaxed">
                {description}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <ChevronDown className="size-4 -rotate-90 text-muted-foreground transition-transform group-data-[panel-open]:rotate-0" />
            </div>
          </CollapsibleTrigger>

          <CollapsibleContent className="border-t p-4">
            {tableSkeleton}
          </CollapsibleContent>
        </Collapsible>
      );
    })}
  </div>
);
